"use client";

import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ElectronUpdateInfo } from "@/types/electron";

// Banner que aparece cuando el wrapper de Electron ya descargó una nueva
// versión. No es modal — el operador puede ignorarlo durante una emergencia
// y volver a él cuando esté libre. Si decide no instalar, el update se
// aplica solo cuando cierre la app (autoInstallOnAppQuit: true en main.js).
export function UpdateBanner() {
  const [info, setInfo] = useState<ElectronUpdateInfo | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.electronAPI) return;
    const unsubscribe = window.electronAPI.onUpdateDownloaded((next) => {
      setInfo(next);
      setDismissed(false);
    });
    return unsubscribe;
  }, []);

  if (!info || dismissed) return null;

  const onInstall = async () => {
    if (!window.electronAPI) return;
    setInstalling(true);
    try {
      await window.electronAPI.installUpdateNow();
    } catch (err) {
      console.error("[update] install failed:", err);
      setInstalling(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 w-[340px] rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-bg-elevated)] shadow-lg"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[var(--color-accent)]/15 text-[var(--color-accent)]">
          <Download className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--color-fg)]">
            Actualización disponible
          </p>
          <p className="mt-1 text-xs text-[var(--color-fg-muted)]">
            Vortigon {info.version} está lista para instalar. La app se va a reiniciar.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" variant="primary" onClick={onInstall} disabled={installing}>
              {installing ? "Instalando…" : "Instalar ahora"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDismissed(true)}>
              Más tarde
            </Button>
          </div>
        </div>
        <button
          type="button"
          aria-label="Cerrar"
          onClick={() => setDismissed(true)}
          className="text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
